import axios, { AxiosError, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { API_BASE_URL } from './constants';
import { storage } from '../services/storage';

// Navigation handler for unauthorized access (platform-specific)
let navigationHandler: (() => void) | null = null;

export const setNavigationHandler = (handler: () => void) => {
  navigationHandler = handler;
};

const handleUnauthorized = () => {
  if (navigationHandler) {
    navigationHandler();
  } else {
    // Fallback for web (window.location is undefined in React Native)
    if (typeof window !== 'undefined' && window.location) {
      window.location.href = '/login';
    }
  }
};

const axiosInstance: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await storage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Skip auth interceptor for login requests — 401 means bad credentials, not expired session
    const isLoginRequest = originalRequest.url?.includes('/auth/api/login');

    if (error.response?.status === 401 && !originalRequest._retry && !isLoginRequest) {
      originalRequest._retry = true;

      const refreshToken = await storage.getItem('refreshToken');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_BASE_URL}/api/auth/refresh`, {
            refreshToken,
          });

          const { token } = response.data;
          await storage.setItem('token', token);

          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }

          return axiosInstance(originalRequest);
        } catch (refreshError) {
          await storage.removeItem('token');
          await storage.removeItem('refreshToken');
          handleUnauthorized();
          return Promise.reject(refreshError);
        }
      } else {
        await storage.removeItem('token');
        handleUnauthorized();
      }
    }

    if (error.response?.status === 403) {
      console.error('Access forbidden: Insufficient permissions');
    }

    if (error.response?.status === 500) {
      console.error('Server error: Please try again later');
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;